import { makeAccess } from '../config/factory-controller'

/**
 * Obtém o conteúdo (HTML ou ZPL) de um comprovante no backend.
 * type param (front usage):
 *  - receiptKind: 'collection-order' | 'journey'
 * mode: 'pdf' => type=0 (HTML)
 *       'zpl' => type=1 (ZPL)
 * backend subtype:
 *  - 0 => CollectionOrderDetail
 *  - 1 => FinishJourney
 */
export async function fetchReceiptContent(
  receiptKind: 'collection-order' | 'journey',
  id: number | string,
  mode: 'pdf' | 'zpl',
): Promise<string> {
  const API_URL =
    import.meta.env.VITE_REACT_APP_API_URL || 'https://localhost:50962'
  const cleanApiUrl = API_URL.replace(/\/+$/, '')

  // Mapear endpoint e subtype
  const isCollection = receiptKind === 'collection-order'
  const endpoint = isCollection
    ? `${cleanApiUrl}/collectionorder/print/${id}`
    : `${cleanApiUrl}/journey/print/${id}`
  const subtype = isCollection ? 0 : 1
  const typeParam = mode === 'pdf' ? 0 : 1

  // Credenciais da aplicação
  const { key: applicationKey, token: applicationToken } = await makeAccess()
  if (!applicationKey || !applicationToken) {
    throw new Error('Credenciales de aplicación faltantes para imprimir.')
  }
  const apiVersion = Number(import.meta.env.VITE_REACT_APP_API_VERSION)

  const params = new URLSearchParams({
    type: String(typeParam),
    subtype: String(subtype),
  })

  const bearer = localStorage.getItem('token')

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-api-version': String(apiVersion),
    'x-application-key': applicationKey,
    'x-application-token': applicationToken,
  }
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`

  const response = await fetch(`${endpoint}?${params.toString()}`, {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    try {
      const errJson = await response.json()
      msg = errJson?.message || msg
    } catch {
      // Ignora erro ao tentar parsear JSON de resposta de erro
    }
    throw new Error(msg)
  }

  // Pode ser texto puro ou JSON
  const text = await response.text()
  // Tenta detectar JSON
  try {
    const parsed = JSON.parse(text)
    if (typeof parsed === 'string') return parsed
    if (parsed?.data) {
      if (typeof parsed.data === 'string') return parsed.data
      if (parsed.data.content) return parsed.data.content
    }
    if (parsed.content) return parsed.content
    // Fallback: stringify
    return JSON.stringify(parsed)
  } catch {
    return text
  }
}
